/**
 * Teamwork Graph CLI OAuth scope catalog.
 * Mirrors the configurable scopes in settings-service / Admin Hub (AFM PR #374724).
 * Grouped into Read, Write and manage, and Delete for display on the consent screen.
 */

export interface TwgScopeEntry {
  id: string;
  scopes: string[];
  label: string;
  description: string;
}

export interface TwgScopeGroup {
  id: 'view' | 'write' | 'delete';
  heading: string;
  groupDescription: string;
  scopes: TwgScopeEntry[];
}

export const HIDDEN_TWG_SCOPE_VALUES: readonly string[] = [
  'offline_access',
  'read:compass:twg-cli',
];
export const HIDDEN_TWG_SCOPES: ReadonlySet<string> = new Set(
  HIDDEN_TWG_SCOPE_VALUES,
);

const scopeEntry = (
  id: string,
  label: string,
  description: string,
  scopes: string[],
): TwgScopeEntry => ({
  id,
  label,
  description,
  scopes,
});

const VIEW_SCOPES: TwgScopeEntry[] = [
  scopeEntry(
    'view-jira',
    'Jira',
    'Jira platform data and Jira Software work, boards, sprints, and agile planning data.',
    ['read:jira-work', 'read:jira-platform:twg-cli', 'read:jsw:twg-cli'],
  ),
  scopeEntry(
    'view-confluence',
    'Confluence',
    'Pages, spaces, comments, and related Confluence content.',
    ['read:confluence:twg-cli'],
  ),
  scopeEntry(
    'view-loom',
    'Loom',
    'Loom videos, transcripts, comments, and related metadata.',
    ['read:loom:twg-cli'],
  ),
  scopeEntry(
    'view-jsm',
    'Jira Service Management',
    'Service desks, help centers, requests, incidents, alerts, and related resources.',
    ['read:jsm:twg-cli'],
  ),
  scopeEntry(
    'view-csm',
    'Customer Service Management',
    'Customer channels, organizations, and service management context.',
    ['read:csm:twg-cli'],
  ),
  scopeEntry(
    'view-assets',
    'Assets',
    'Assets objects, schemas, attributes, and related metadata.',
    ['read:assets:twg-cli'],
  ),
  scopeEntry(
    'view-focus-areas',
    'Focus areas',
    'Focus areas, risks, status updates, and related planning context.',
    ['read:jira-align:twg-cli', 'read:mercury:twg-cli'],
  ),
  scopeEntry(
    'view-talent',
    'Talent',
    'People, roles, headcount, and workforce planning data.',
    ['read:radar:twg-cli'],
  ),
  scopeEntry(
    'view-projects-goals',
    'Projects and goals',
    'Projects, goals, status, ownership, and related planning details.',
    ['read:goal:townsquare', 'read:project:townsquare'],
  ),
  scopeEntry(
    'view-users-teams',
    'Users and teams',
    'User profiles, teams, and team memberships.',
    ['read:account', 'view:membership:teams', 'view:team:teams'],
  ),
  scopeEntry(
    'view-platform-context',
    'Platform and context',
    'Teamwork Graph context and data from third-party tools connected to your Atlassian workspace.',
    ['read:3p-data:twg-cli', 'read:teamwork-graph:twg-cli'],
  ),
  scopeEntry(
    'view-trello',
    'Trello',
    'Boards, cards, lists, and related Trello data.',
    ['read:trello:twg-cli'],
  ),
  scopeEntry(
    'view-feedback',
    'Feedback',
    'View feedback records, fields, insights, snippets, sources, customers, and views.',
    ['read:feedback:twg-cli'],
  ),
];

const WRITE_SCOPES: TwgScopeEntry[] = [
  scopeEntry(
    'write-jira',
    'Jira',
    'Jira platform data and Jira Software work, boards, sprints, and agile planning data.',
    ['write:jira-platform:twg-cli', 'write:jsw:twg-cli'],
  ),
  scopeEntry(
    'write-confluence',
    'Confluence',
    'Pages, spaces, comments, and related Confluence content.',
    ['write:confluence:twg-cli'],
  ),
  scopeEntry(
    'write-loom',
    'Loom',
    'Loom videos, transcripts, comments, and related metadata.',
    ['write:loom:twg-cli'],
  ),
  scopeEntry(
    'write-jsm',
    'Jira Service Management',
    'Service desks, help centers, requests, incidents, alerts, and related resources.',
    ['write:jsm:twg-cli'],
  ),
  scopeEntry(
    'write-assets',
    'Assets',
    'Assets objects, schemas, attributes, and related metadata.',
    ['write:assets:twg-cli'],
  ),
  scopeEntry(
    'write-focus-areas',
    'Focus areas',
    'Focus areas, risks, status updates, and related planning context.',
    ['write:mercury:twg-cli'],
  ),
  scopeEntry(
    'write-talent',
    'Talent',
    'People, roles, headcount, and workforce planning data.',
    ['write:radar:twg-cli'],
  ),
  scopeEntry(
    'write-projects-goals',
    'Projects and goals',
    'Projects, goals, status, ownership, and related planning details.',
    ['write:goal:townsquare', 'write:project:townsquare'],
  ),
  scopeEntry(
    'write-users-teams',
    'Users and teams',
    'User profiles, teams, and team memberships.',
    ['write:membership:teams', 'write:team:teams'],
  ),
  scopeEntry(
    'write-platform-context',
    'Platform and context',
    'Teamwork Graph context and data from third-party tools connected to your Atlassian workspace.',
    ['write:all:twg'],
  ),
  scopeEntry(
    'write-trello',
    'Trello',
    'Boards, cards, lists, and related Trello data.',
    ['write:trello:twg-cli'],
  ),
  scopeEntry(
    'write-feedback',
    'Feedback',
    'Create and update feedback records, fields, insights, snippets, sources, customers, and views.',
    ['write:feedback:twg-cli'],
  ),
];

const DELETE_SCOPES: TwgScopeEntry[] = [
  scopeEntry(
    'delete-jira',
    'Jira',
    'Jira platform data and Jira Software work, boards, sprints, and agile planning data.',
    ['delete:jira-platform:twg-cli', 'delete:jsw:twg-cli'],
  ),
  scopeEntry(
    'delete-confluence',
    'Confluence',
    'Pages, spaces, comments, and related Confluence content.',
    ['delete:confluence:twg-cli'],
  ),
  scopeEntry(
    'delete-loom',
    'Loom',
    'Loom videos, transcripts, comments, and related metadata.',
    ['delete:loom:twg-cli'],
  ),
  scopeEntry(
    'delete-jsm',
    'Jira Service Management',
    'Service desks, help centers, requests, incidents, alerts, and related resources.',
    ['delete:jsm:twg-cli'],
  ),
  scopeEntry(
    'delete-assets',
    'Assets',
    'Assets objects, schemas, attributes, and related metadata.',
    ['delete:assets:twg-cli'],
  ),
  scopeEntry(
    'delete-focus-areas',
    'Focus areas',
    'Focus areas, risks, status updates, and related planning context.',
    ['delete:mercury:twg-cli'],
  ),
  scopeEntry(
    'delete-feedback',
    'Feedback',
    'Permanently delete feedback records, insights, and snippets.',
    ['delete:feedback:twg-cli'],
  ),
];

// Lookup map: scope string to catalog entry (across all groups)
const ALL_SCOPES: TwgScopeEntry[] = [
  ...VIEW_SCOPES,
  ...WRITE_SCOPES,
  ...DELETE_SCOPES,
];
export const SCOPE_BY_VALUE: ReadonlyMap<string, TwgScopeEntry> = new Map(
  ALL_SCOPES.flatMap((entry) => entry.scopes.map((scope) => [scope, entry])),
);

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export const TWG_SCOPE_GROUPS: readonly TwgScopeGroup[] = [
  {
    id: 'view',
    heading: 'Read',
    groupDescription:
      'Allow Teamwork Graph CLI to view and read data from connected apps.',
    scopes: VIEW_SCOPES,
  },
  {
    id: 'write',
    heading: 'Write and manage',
    groupDescription:
      'Allow Teamwork Graph CLI to create, update, and manage data in connected apps.',
    scopes: WRITE_SCOPES,
  },
  {
    id: 'delete',
    heading: 'Delete',
    groupDescription:
      'Allow Teamwork Graph CLI to permanently delete data from connected apps.',
    scopes: DELETE_SCOPES,
  },
];
